import { scry, scryImageUrl } from "@/lib/scryClient";
import * as scryApi from "@/lib/scryApiClient";

export type NormalCard = {
  name: string;
  count: number;
  scryfall_id?: string;
  oracle_id?: string;
  set?: string | null;
  number?: string | null;
  image_normal?: string | null;
  image_small?: string | null;
  image_large?: string | null;
  layout?: string | null;
  type_line?: string | null;
  colors?: string[] | null;
  color_identity?: string[] | null;
  mana_value?: number | null;
  oracle_text?: string | null;
  foil?: boolean; // parsed foil flag (from *F*)
};

export type NormalDeck = {
  title?: string;
  format?: string | null;
  commander?: string[];
  companion?: string | null;
  main: NormalCard[];
  side?: NormalCard[];
  maybeboard?: NormalCard[];
  raw?: unknown;
};

export type DetectedSource =
  | "archidekt"
  | "moxfield"
  | "tappedout"
  | "manabox"
  | "aetherhub"
  | "tcgplayer"
  | "edhrec";

const RX = {
  archidekt: /https?:\/\/(?:www\.)?archidekt\.com\/decks\/(\d+)/i,
  moxfield: /https?:\/\/(?:www\.)?moxfield\.com\/decks\/([A-Za-z0-9_\-]+)/i,
  tappedout: /https?:\/\/(?:www\.)?tappedout\.net\/mtg-decks\/[^/?#]+/i,
  manabox: /https?:\/\/(?:www\.)?manabox\.app\/decks\/([A-Za-z0-9_\-]+)/i,
  aetherhub: /https?:\/\/(?:www\.)?aetherhub\.com\/Deck\/[A-Za-z0-9\-]+/i,
  // UPDATED: broader TCGplayer + infinite subdomain + multiple deck URL shapes
  tcgplayer: /https?:\/\/(?:www\.)?(?:tcgplayer\.com|infinite\.tcgplayer\.com)\/(?:(?:content\/magic-the-gathering\/deck\/[^/]+\/\d+)|(?:deck\/details\/\d+)|(?:magic\/deck\/show\?deckid=\d+)|(?:magic\/decklist\/[^/]+\/\d+))/i,
  edhrec: /https?:\/\/(?:www\.)?edhrec\.com\/(?:deckpreview|deck)\/[A-Za-z0-9_\-]+/i,
};

export function detectDeckSource(url: string): { kind: DetectedSource; id?: string } | null {
  const arch = url.match(RX.archidekt); if (arch) return { kind: "archidekt", id: arch[1] };
  const mox = url.match(RX.moxfield); if (mox) return { kind: "moxfield", id: mox[1] };
  if (RX.tappedout.test(url)) return { kind: "tappedout" };
  const mbx = url.match(RX.manabox); if (mbx) return { kind: "manabox", id: mbx[1] };
  if (RX.aetherhub.test(url)) return { kind: "aetherhub" };
  if (RX.tcgplayer.test(url)) return { kind: "tcgplayer" };
  if (RX.edhrec.test(url))     return { kind: "edhrec" };
  return null;
}

const ALLOW_HOST = [
  "archidekt.com","moxfield.com","tappedout.net","manabox.app","aetherhub.com",
  "tcgplayer.com","infinite.tcgplayer.com","edhrec.com",
];

// Prefer env override; fallback to dev alias path you can proxy via Vite
function resolveDeckProxyBase(): string {
  const env: any = (import.meta as any).env || {};
  let base: string = env?.VITE_DECK_PROXY_URL || "/__deck_proxy__";
  // Prevent mixed-content in prod
  if (/^http:\/\//i.test(base) && globalThis.location?.protocol === "https:") {
    console.warn("[deckImport] insecure VITE_DECK_PROXY_URL on https site; falling back to '/__deck_proxy__'");
    base = "/__deck_proxy__";
  }
  return base;
}
const PROXY_BASE = resolveDeckProxyBase();

async function proxiedFetchText(url: string): Promise<string> {
  const host = new URL(url).hostname.replace(/^www\./, "");
  if (!ALLOW_HOST.some(h => host.endsWith(h))) throw new Error("Blocked host: " + host);
  const r = await fetch(`${PROXY_BASE}?url=${encodeURIComponent(url)}`, { credentials: "omit" });
  if (!r.ok) throw new Error(`Fetch failed: ${r.status}`);
  return await r.text();
}
async function proxiedFetchJson<T>(url: string): Promise<T> {
  const txt = await proxiedFetchText(url);
  // If proxy returned HTML (likely error page), surface a clear error
  if (/^\s*<!doctype html>|^\s*<html[\s>]/i.test(txt)) {
    throw new Error("Proxy returned HTML (upstream blocked). Check /__deck_proxy__ rewrite is deployed.");
  }
  try { return JSON.parse(txt) as T; } catch { throw new Error("Invalid JSON from " + url); }
}
function stripTags(html: string): string {
  return html.replace(/<script[\s\S]*?<\/script>/gi,"")
             .replace(/<style[\s\S]*?<\/style>/gi,"")
             .replace(/<[^>]+>/g,"\n");
}

// HTML entity unescape helper
function htmlUnescape(s: string): string {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#8217;|&#x2019;/gi, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ");
}

// Detect section headers like "Creature (25)", "Cards (60 distinct)" etc.
function looksLikeSectionHeader(name: string): boolean {
  const n = name.toLowerCase().trim();
  if (!n) return false;
  if (/\bdistinct\b/.test(n)) return true;
  const core = n.replace(/\([^)]*\)/g, "").trim();
  return /^(cards?|creature|instant|sorcery|land|artifact|enchantment|planeswalker|battle|legendary|sideboard|maybeboard|commander|companions?|main(?:board)?|main\s*deck)\b/.test(core);
}

// Make hidden <span>/<td> qty + following text look like "4 Card Name"
function collapseQuantityNamePairs(html: string): string {
  let h = html;

  // 🚫 Drop parenthesized section totals like "(32)" so they don’t get misread as “qty”
  h = h.replace(/<span[^>]*>\s*\(\s*\d+\s*\)\s*<\/span>/gi, "");

  // TCGplayer: <span class="list__item-quanity|quantity">4</span> Card Name
  h = h.replace(
    /<span[^>]*class="[^"]*list__item-quan(?:tity|ity)[^"]*"[^>]*>\s*(\d+)\s*<\/span>\s*(?:<\/?[^>]+>)*\s*([^<][^<]*)/gi,
    (_m, n, name) => `${n} ${htmlUnescape(name)}\n`
  );

  // Generic <li> ... <span>4</span> ... Card Name ... </li>
  h = h.replace(
    /<li[^>]*>(?:(?:(?!<\/li>)[\s\S])*?<span[^>]*>\s*(\d+)\s*<\/span>[\s\S]*?)?\s*(?:<a[^>]*>)?\s*([^<][^<]+?)\s*(?:<\/a>)?[\s\S]*?<\/li>/gi,
    (m, n, name) => {
      const cnt = n ? Number(n) : NaN;
      if (!cnt || looksLikeSectionHeader(name)) return m;
      return `${cnt} ${htmlUnescape(name)}\n`;
    }
  );

  // Table rows: <tr><td>4</td><td>Card Name</td>...</tr>
  h = h.replace(
    /<tr[^>]*>\s*<t[dh][^>]*>\s*(\d+)\s*<\/t[dh]>\s*<t[dh][^>]*>\s*(?:<a[^>]*>)?\s*([^<][^<]+?)\s*(?:<\/a>)?\s*<\/t[dh]>[\s\S]*?<\/tr>/gi,
    (_m, n, name) => `${n} ${htmlUnescape(name)}\n`
  );

  // Class names like qty|quant|count
  h = h.replace(
    /<(?:span|div)[^>]+(?:class|data-[^=]+)="[^"]*(?:qty|quan|quant|count)[^"]*"[^>]*>\s*(\d+)\s*<\/(?:span|div)>\s*(?:<\/?[^>]+>)*\s*(?:<a[^>]*>)?\s*([^<]{2,})/gi,
    (_m, n, name) => `${n} ${htmlUnescape(name)}\n`
  );

  // NEW generic: qty cell then <a>Card Name</a>
  h = h.replace(
    /<(?:span|div|td|th)[^>]*>\s*(\d+)\s*(?:x)?\s*<\/(?:span|div|td|th)>\s*(?:<\/?[^>]+>)*\s*(?:<a[^>]*>)\s*([^<][^<]+?)\s*<\/a>/gi,
    (_m, n, name) => `${n} ${htmlUnescape(name)}\n`
  );
  // NEW generic: qty cell then plaintext name
  h = h.replace(
    /<(?:span|div|td|th)[^>]*>\s*(\d+)\s*(?:x)?\s*<\/(?:span|div|td|th)>\s*(?:<\/?[^>]+>)*\s*([^<][^<]+?)(?:<|$)/gi,
    (_m, n, name) => `${n} ${htmlUnescape(name)}\n`
  );

  return h;
}

// REPLACED: extractCardsFromEmbeddedJSON (now uses MAX per duplicate name)
function extractCardsFromEmbeddedJSON(html: string): {
  main: NormalCard[];
  side: NormalCard[];
  maybe: NormalCard[];
} {
  const scripts = [...html.matchAll(/<script[^>]*>([\s\S]*?)<\/script>/gi)].map(m => m[1] || "");
  const seenNodes = new Set<any>();

  const mkAgg = () => new Map<string, NormalCard>();
  const aggMain = mkAgg();
  const aggSide = mkAgg();
  const aggMaybe = mkAgg();

  const cleanName = (s: any) => (typeof s === "string" ? s.trim() : "");
  const keyFor = (n: string) => n.toLowerCase();

  // REPLACED add() inside extractCardsFromEmbeddedJSON to strip leading counts & normalize
  const add = (bucket: Map<string, NormalCard>, name: any, qty: any) => {
    let n = cleanName(name);
    if (!n) return;
    // Strip any leading count that slipped into name
    n = stripLeadingCount(n);
    n = normalizeApostrophes(n);
    const qn = Number(qty);
    if (!n || !qn || looksLikeSectionHeader(n)) return;
    const key = keyFor(n);
    const existing = bucket.get(key);
    if (existing) {
      existing.count = Math.max(existing.count, qn);
    } else {
      bucket.set(key, { name: n, count: qn });
    }
  };

  const boardFor = (path: string[]): Map<string, NormalCard> => {
    const p = path.join("/").toLowerCase();
    if (/(^|\/)side(board)?(\/|$)/.test(p)) return aggSide;
    if (/(^|\/)maybe(board)?(\/|$)/.test(p)) return aggMaybe;
    return aggMain;
  };

  const walk = (node: any, path: string[] = []) => {
    if (!node || typeof node !== "object") return;
    if (seenNodes.has(node)) return;
    seenNodes.add(node);

    const bucket = boardFor(path);

    const q =
      (node as any).quantity ??
      (node as any).qty ??
      (node as any).count ??
      (node as any).amount;

    let nm: any =
      (node as any).card && typeof (node as any).card === "object"
        ? (node as any).card.name ??
          (node as any).card.faceName ??
          (node as any).card.cardName
        : undefined;

    if (!nm && typeof (node as any).name === "string") {
      nm = (node as any).name;
    }

    if (nm && q != null) add(bucket, nm, q);

    for (const [k, v] of Object.entries(node)) {
      if (Array.isArray(v)) {
        for (let i = 0; i < v.length; i++) walk(v[i], [...path, k, String(i)]);
      } else if (v && typeof v === "object") {
        walk(v, [...path, k]);
      }
    }
  };

  const tryParse = (s: string) => {
    const assignMatch = s.match(
      /(?:__NUXT__|__NEXT_DATA__|window\.[A-Za-z0-9_$]+)\s*=\s*(\{[\s\S]*?\})\s*;?/
    );
    if (assignMatch) {
      try { walk(JSON.parse(assignMatch[1])); } catch {}
    }
    const trimmed = s.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try { walk(JSON.parse(trimmed)); } catch {}
    }
  };

  scripts.forEach(tryParse);

  const toArray = (m: Map<string, NormalCard>) => [...m.values()];
  return { main: toArray(aggMain), side: toArray(aggSide), maybe: toArray(aggMaybe) };
}

// NEW: helper to dedupe by name keeping max count
function dedupeByNameMax(cards: NormalCard[] | undefined): NormalCard[] | undefined {
  if (!cards || !cards.length) return cards;
  const map = new Map<string, NormalCard>();
  for (const c of cards) {
    const k = c.name.toLowerCase();
    const ex = map.get(k);
    if (!ex) map.set(k, { ...c });
    else ex.count = Math.max(ex.count, c.count);
  }
  return [...map.values()];
}

// Deck-wide dedupe (unique card name per board; keep max count & merge metadata)
function dedupeCardArray(cards: NormalCard[] | undefined): NormalCard[] | undefined {
  if (!cards || !cards.length) return cards;
  const map = new Map<string, NormalCard>();
  for (const c of cards) {
    const key = c.name.toLowerCase();
    const ex = map.get(key);
    if (!ex) {
      map.set(key, { ...c });
    } else {
      ex.count = Math.max(ex.count, c.count);
      ex.foil = ex.foil || c.foil;
      if (!ex.set && c.set) ex.set = c.set;
      if (!ex.number && c.number) ex.number = c.number;
      if (!ex.image_small && c.image_small) ex.image_small = c.image_small;
      if (!ex.image_normal && c.image_normal) ex.image_normal = c.image_normal;
      if (!ex.image_large && c.image_large) ex.image_large = c.image_large;
      if (!ex.layout && c.layout) ex.layout = c.layout;
      if (!ex.type_line && c.type_line) ex.type_line = c.type_line;
      if (!ex.colors && c.colors) ex.colors = c.colors;
      if (!ex.color_identity && c.color_identity) ex.color_identity = c.color_identity;
      if (ex.mana_value == null && c.mana_value != null) ex.mana_value = c.mana_value;
      if (!ex.oracle_text && c.oracle_text) ex.oracle_text = c.oracle_text;
      if (!ex.scryfall_id && c.scryfall_id) ex.scryfall_id = c.scryfall_id;
      if (!ex.oracle_id && c.oracle_id) ex.oracle_id = c.oracle_id;
    }
  }
  return [...map.values()];
}
function dedupeDeck(deck: NormalDeck): NormalDeck {
  return {
    ...deck,
    main: dedupeCardArray(deck.main) || [],
    side: dedupeCardArray(deck.side),
    maybeboard: dedupeCardArray(deck.maybeboard),
  };
}

/* ------------------- Site parsers ------------------- */
async function parseArchidekt(_url: string, id: string): Promise<NormalDeck> {
  type ArchCard = { quantity: number; categories: string[]; card: { oracleCard?: { name: string } } };
  type ArchDeck = { name: string; format?: string | null; cards: ArchCard[]; };
  const data = await proxiedFetchJson<ArchDeck>(`https://archidekt.com/api/decks/${id}/`);
  const has = (tag: string) => (c: ArchCard) => (c.categories||[]).some(cat => cat.toLowerCase() === tag.toLowerCase());
  const pick = (pred: (c: ArchCard)=>boolean) =>
    data.cards.filter(pred).map(c => ({
      name: c.card?.oracleCard?.name ?? "",
      count: Number(c.quantity)||1,
    })).filter(c => c.name);
  const isSide = has("Sideboard");
  const isMaybe = has("Maybeboard");
  const main = data.cards.filter(c => !isSide(c) && !isMaybe(c));
  return {
    title: data.name,
    format: data.format ?? null,
    main: pick(c => main.includes(c)),
    side: pick(isSide),
    maybeboard: pick(isMaybe),
    raw: data,
  };
}

async function parseMoxfield(url: string): Promise<NormalDeck> {
  const id = url.match(RX.moxfield)?.[1];
  if (!id) throw new Error("Bad Moxfield URL");
  const data = await proxiedFetchJson<any>(`https://api.moxfield.com/v2/decks/all/${id}`);
  const collect = (board: any): NormalCard[] =>
    board
      ? Object.values(board).map((e: any) => {
          const card = e?.card || {};
          const name = card?.name || e?.name || "";
          if (!name) return null;
          return {
            name,
            count: Number(e?.quantity ?? e?.qty ?? 1) || 1,
            scryfall_id: card?.scryfall_id || card?.scryfallId || undefined,
            oracle_id: card?.oracle_id || card?.oracleId || undefined,
            set: card?.set || null,
            number: card?.collector_number || card?.collectorNumber || null,
            image_normal: card?.image_uris?.normal || null,
            image_small: card?.image_uris?.small || null,
            image_large: card?.image_uris?.large || null,
            layout: card?.layout || null,
            type_line: card?.type_line || null,
            colors: Array.isArray(card?.colors) ? card.colors : null,
            color_identity: Array.isArray(card?.color_identity) ? card.color_identity : null,
            mana_value: typeof card?.mana_value === "number" ? card.mana_value : (typeof card?.cmc === "number" ? card.cmc : null),
            oracle_text: card?.oracle_text || null,
          } as NormalCard;
        })
        .filter(Boolean) as NormalCard[]
      : [];
  const commanders = collect(data.commanders || data.commander).map(c => c.name);
  const companion =
    (Object.values(data.companions || {} as any)
      .map((c: any)=> c?.card?.name || c?.name || "")
      .filter(Boolean)[0]) || null;
  return {
    title: data.name || data.publicId || id,
    format: data.format || data.formatName || null,
    commander: commanders.length ? commanders : undefined,
    companion: companion || undefined,
    main: collect(data.mainboard),
    side: collect(data.sideboard),
    maybeboard: collect(data.maybeboard),
    raw: { id, api: "moxfield" }
  };
}

async function parseTappedOut(url: string): Promise<NormalDeck> {
  const tries = [`${url}?fmt=txt`,`${url}?format=txt`, `${url.replace(/\/$/,"")}/?fmt=txt`];
  for (const t of tries) {
    try {
      const txt = await proxiedFetchText(t);
      if (/\d+\s+.+/.test(txt)) return { main: parseCountNameLines(txt) };
    } catch {}
  }
  const html = await proxiedFetchText(url);
  const slab = stripTags(html);
  const arenaBlock = (slab.split(/MTG\s+Arena/i)[1] || "").split(/(?:Mainboard|Sideboard|Maybeboard)/i)[0] || "";
  const main = parseCountNameLines(arenaBlock || slab);
  return { main };
}

/* ================= Unified fetch that understands SSR JSON ({ html }) ================= */
async function fetchPageHtmlWithSSR(url: string): Promise<string> {
  const RENDER_BASE = (import.meta as any).env?.VITE_DECK_RENDER_URL || null;
  if (!RENDER_BASE) {
    return await proxiedFetchText(url);
  }
  try {
    const r = await fetch(`${RENDER_BASE}?url=${encodeURIComponent(url)}`, { credentials: "omit" });
    if (!r.ok) throw new Error("SSR fetch failed");
    const ct = r.headers.get("content-type") || "";
    const body = await r.text();
    if (ct.includes("application/json") || body.trim().startsWith("{")) {
      try {
        const j = JSON.parse(body);
        if (j?.html && typeof j.html === "string") return j.html;
      } catch { /* fallthrough */ }
    }
    return body; // already HTML
  } catch {
    return await proxiedFetchText(url);
  }
}
function unwrapMaybeHtml(body: string): string {
  if (body.trim().startsWith("{")) {
    try {
      const j = JSON.parse(body);
      if (j?.html) return j.html;
    } catch {}
  }
  return body;
}

/* ================= ManaBox parser ================= */
async function parseManaBox(url: string): Promise<NormalDeck> {
  const htmlRaw = await fetchPageHtmlWithSSR(url);
  const html = unwrapMaybeHtml(htmlRaw);

  // 1) Embedded JSON (Nuxt/SPA)
  const fromJson = extractCardsFromEmbeddedJSON(html);
  if (fromJson.main.length || fromJson.side.length || fromJson.maybe.length) {
    return {
      main: dedupeByNameMax(fromJson.main)!,
      side: dedupeByNameMax(fromJson.side || undefined),
      maybeboard: dedupeByNameMax(fromJson.maybe || undefined),
    };
  }

  // 2) Export blocks
  const exportBlocks = [
    ...html.matchAll(/<(?:textarea|pre|code)[^>]*>([\s\S]*?)<\/(?:textarea|pre|code)>/gi)
  ].map(m => m[1]);
  for (const block of exportBlocks) {
    const txt = htmlUnescape(block.replace(/<[^>]+>/g, " "));
    const main = parseCountNameLines(txt);
    if (main.length) return { main };
  }

  // 3) Collapse & parse sections
  const collapsed = collapseQuantityNamePairs(html);
  const slab = stripTags(collapsed);
  const sectMain =
    extractSectionCards(slab, /(Main\s*Deck|Mainboard|Maindeck|Decklist|Main\b)/i) ||
    parseCountNameLines(slab);
  const sectSide = extractSectionCards(slab, /(Sideboard|Side\s*board|Side\b)/i) || undefined;
  const sectMaybe = extractSectionCards(slab, /(Maybeboard|Maybe\s*board)/i) || undefined;
  if (sectMain.length) {
    return {
      main: sectMain,
      side: sectSide && sectSide.length ? sectSide : undefined,
      maybeboard: sectMaybe && sectMaybe.length ? sectMaybe : undefined,
    };
  }

  // 4) Rough sweep
  const rough = (slab.match(/^(?:\d+\s+[^\n]+)$/gmi) || []).join("\n");
  const main = parseCountNameLines(rough);
  if (main.length) return { main };

  throw new Error("ManaBox: no cards found");
}

/* ================= TCGplayer parser ================= */
async function parseTCGplayer(url: string): Promise<NormalDeck> {
  const htmlRaw = await fetchPageHtmlWithSSR(url);
  const html = unwrapMaybeHtml(htmlRaw);

  // A) Direct DOM scrape
  const direct = extractTCGplayerCards(html); // assumed existing in your codebase
  if (direct.main.length) return direct;

  // B) Embedded JSON
  const fromJson = extractCardsFromEmbeddedJSON(html);
  if (fromJson.main.length || fromJson.side.length) {
    return {
      main: fromJson.main,
      side: fromJson.side.length ? fromJson.side : undefined
    };
  }

  // C) Collapse, then strip & section parse
  const collapsed = collapseQuantityNamePairs(html);
  const slab = stripTags(collapsed);

  const take = (hdr: string) => {
    const seg = (slab.split(new RegExp(hdr, "i"))[1] || "")
      .split(/(?:Mainboard|Main\s*Deck|Sideboard|Maybeboard|Strategy|$)/i)[0] || "";
    return parseCountNameLines(seg);
  };

  const main = take("Mainboard|Main\\s*Deck|Decklist|Main\\b") || [];
  const side = take("Sideboard|Side\\b") || [];

  if (main.length || side.length) {
    return { main, side: side.length ? side : undefined };
  }

  // D) Loose final sweep over collapsed text
  const sweep = parseCountNameLines(slab);
  if (sweep.length) return { main: sweep };

  throw new Error("TCGplayer: no cards found (check SSR or page changed)");
}

/* ================= AetherHub parser ================= */
async function parseAetherHub(url: string): Promise<NormalDeck> {
  const htmlRaw = await fetchPageHtmlWithSSR(url);
  const html = unwrapMaybeHtml(htmlRaw);

  // 1) Embedded JSON
  const jsonCards = extractCardsFromEmbeddedJSON(html);
  if (jsonCards.main.length || jsonCards.side.length || jsonCards.maybe.length) {
    return {
      main: jsonCards.main,
      side: jsonCards.side.length ? jsonCards.side : undefined,
      maybeboard: jsonCards.maybe.length ? jsonCards.maybe : undefined,
    };
  }

  // 2) Export/code/pre blocks
  const exportBlocks = [
    ...html.matchAll(/<(?:textarea|pre|code)[^>]*>([\s\S]*?)<\/(?:textarea|pre|code)>/gi)
  ].map(m => m[1]);
  for (const block of exportBlocks) {
    const txt = htmlUnescape(block.replace(/<[^>]+>/g, " "));
    if (/\d+\s+[A-Za-z]/.test(txt)) {
      const main = parseCountNameLines(txt);
      if (main.length) return { main };
    }
  }

  // 3) Collapse quantity/name patterns + sections
  const collapsed = collapseQuantityNamePairs(html);
  const slab = stripTags(collapsed);
  const sectMain =
    extractSectionCards(slab, /(Mainboard|Main\s*Deck|Maindeck|Decklist|Main\b)/i) ||
    parseCountNameLines(slab);
  const sectSide = extractSectionCards(slab, /(Sideboard|Side\s*board|Side\b)/i) || undefined;
  const sectMaybe = extractSectionCards(slab, /(Maybeboard|Maybe\s*board)/i) || undefined;
  if (sectMain.length) {
    return {
      main: sectMain,
      side: sectSide && sectSide.length ? sectSide : undefined,
      maybeboard: sectMaybe && sectMaybe.length ? sectMaybe : undefined,
    };
  }

  // 4) Rough sweep
  const rough = (slab.match(/^(?:\d+\s+[^\n]+)$/gmi) || []).join("\n");
  const main = parseCountNameLines(rough);
  if (main.length) return { main };

  throw new Error("AetherHub: no cards found");
}

/* ================= EDHREC: direct tile scrape helper ================= */
function extractEDHRECDirect(html: string): NormalCard[] {
  const names = new Set<string>();
  const out: NormalCard[] = [];

  // Example: <span class="Card_name__Mpa7S">Brash Taunter</span>
  const rxName = /<span[^>]*class="[^"]*Card_name__[^"]*"[^>]*>\s*([^<]+?)\s*<\/span>/gi;
  let m: RegExpExecArray | null;
  while ((m = rxName.exec(html))) {
    const raw = m[1] || "";
    const name = normalizeApostrophes(stripLeadingCount(htmlUnescape(raw))).trim();
    if (!name) continue;
    if (looksLikeSectionHeader(name)) continue;
    const key = name.toLowerCase();
    if (!names.has(key)) {
      names.add(key);
      out.push({ name, count: 1 });
    }
  }
  return out;
}

/* ================= EDHREC parser ================= */
async function parseEDHREC(url: string): Promise<NormalDeck> {
  // Try SSR first (works great if you already have VITE_DECK_RENDER_URL),
  // otherwise fall back to proxied HTML.
  const htmlRaw = await fetchPageHtmlWithSSR(url);
  const html    = unwrapMaybeHtml(htmlRaw);

  // A0) EDHREC tile scrape: extremely reliable on deck previews/lists
  const directMain = extractEDHRECDirect(html);
  if (directMain.length) {
    const slab = stripTags(html);
    const commanders = extractSectionNames(slab, /(Commander|Commanders)/i);
    const titleMatch = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    const title = titleMatch ? htmlUnescape(titleMatch[1]).replace(/\s*\|\s*EDHREC[\s\S]*$/i, "").trim() : undefined;

    return dedupeDeck({
      title,
      commander: commanders.length ? commanders : undefined,
      main: directMain,
      raw: { source: "edhrec-direct", url },
    });
  }

  // A) Some EDHREC pages are Nuxt apps — try harvesting embedded JSON first.
  const fromJson = extractCardsFromEmbeddedJSON(html);
  if (fromJson.main.length || fromJson.side.length || fromJson.maybe.length) {
    const slab = stripTags(html);
    const commanders = extractSectionNames(slab, /(Commander|Commanders)/i);
    const title = (() => {
      const m = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
      return m ? htmlUnescape(m[1]).replace(/\s*\|\s*EDHREC[\s\S]*$/i, "").trim() : undefined;
    })();
    return dedupeDeck({
      title,
      commander: commanders.length ? commanders : undefined,
      main: dedupeByNameMax(fromJson.main) || [],
      side: dedupeByNameMax(fromJson.side) || undefined,
      maybeboard: dedupeByNameMax(fromJson.maybe) || undefined,
      raw: { source: "edhrec-json", url },
    });
  }

  // B) If they render a text export block anywhere (<pre>, <code>, <textarea>), use it.
  const exportBlocks = [
    ...html.matchAll(/<(?:textarea|pre|code)[^>]*>([\s\S]*?)<\/(?:textarea|pre|code)>/gi),
  ].map(m => m[1]);
  for (const block of exportBlocks) {
    const txt  = htmlUnescape(block.replace(/<[^>]+>/g, " "));
    const main = parseCountNameLines(txt);
    if (main.length) {
      const slab = stripTags(html);
      const commanders = extractSectionNames(slab, /(Commander|Commanders)/i);
      return dedupeDeck({
        commander: commanders.length ? commanders : undefined,
        main,
        raw: { source: "edhrec-export", url },
      });
    }
  }

  // C) Collapse common quantity/name DOM patterns, then section-parse.
  const collapsed = collapseQuantityNamePairs(html);
  const slab      = stripTags(collapsed);

  // Commander(s) are usually clearly labeled on EDHREC
  const commanders = extractSectionNames(slab, /(Commander|Commanders)/i);

  // EDHREC might not label "Mainboard", but does show typed sections — we’ll
  // take everything numeric as main, and pick Sideboard/Maybeboard if present.
  const main =
    extractSectionCards(slab, /(Mainboard|Main\s*Deck|Maindeck|Decklist|Creatures|Nonland|Lands|Artifacts|Instants|Sorceries|Enchantments|Planeswalkers|Battles)/i)
    || parseCountNameLines(slab);

  const side  = extractSectionCards(slab, /(Sideboard|Side\s*board|Side\b)/i) || undefined;
  const maybe = extractSectionCards(slab, /(Maybeboard|Maybe\s*board)/i)      || undefined;

  const title = (() => {
    const m = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    return m ? htmlUnescape(m[1]).replace(/\s*\|\s*EDHREC[\s\S]*$/i, "").trim() : undefined;
  })();

  if (!main.length && !side?.length && !maybe?.length) {
    throw new Error("EDHREC: no cards found");
  }

  return dedupeDeck({
    title,
    commander: commanders.length ? commanders : undefined,
    main,
    side: side?.length ? side : undefined,
    maybeboard: maybe?.length ? maybe : undefined,
    raw: { source: "edhrec", url },
  });
}


/* ================= Robust text line parser ================= */
// Normalize apostrophes / curly quotes / doubled quotes in card names
function normalizeApostrophes(s: string): string {
  return s
    .replace(/&(?:#x27|#39|#x2019|#8217);/gi, "'")
    .replace(/[’‘`´]/g, "'")
    .replace(/''+/g, "'")
    .replace(/'{2,}/g, "'");
}

// NEW: strip any leading embedded count (e.g. "4 Ancient Tomb", "2x Sol Ring")
function stripLeadingCount(name: string): string {
  return name.replace(/^\s*\d+\s*x?\s+/i, "");
}

function parseCountNameLines(text: string): NormalCard[] {
  const out: NormalCard[] = [];
  const lines = text.replace(/\u00A0/g, " ").split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    let line = (lines[i] || "").trim();
    if (!line) continue;

    const loneCount = line.match(/^(\d+)\s*x?$/i);
    if (loneCount) {
      let j = i + 1;
      while (j < lines.length && !String(lines[j] || "").trim()) j++;
      if (j < lines.length) {
        const next = String(lines[j]).trim();
        if (next && !looksLikeSectionHeader(next)) {
          line = `${loneCount[1]} ${next}`;
          i = j;
        }
      }
    }

    if (!/^\d+\s*(x|\b)/i.test(line)) continue;

    let foil = false;
    if (/\*F\*/i.test(line)) {
      foil = true;
      line = line.replace(/\*F\*/ig, " ").trim();
    }

    const mCount = line.match(/^(\d+)\s*(?:x)?\s+(.*)$/i);
    if (!mCount) continue;
    const count = Number(mCount[1]);
    if (!count) continue;
    let rest = mCount[2].trim();
    if (!rest || looksLikeSectionHeader(rest)) continue;

    let set: string | null = null;
    const mSet = rest.match(/\(([A-Z0-9]{2,5})\)/i);
    if (mSet) {
      set = mSet[1].toUpperCase();
      rest = (rest.slice(0, mSet.index) + rest.slice((mSet.index || 0) + mSet[0].length)).trim();
    }

    const parts = rest.split(/\s+/);
    if (!parts.length) continue;

    let collector: string | null = null;
    const last = parts[parts.length - 1];
    const rxNum = /^(\d+[a-z]?)$/i;
    const rxSetNum = /^([A-Z0-9]{2,5})[-–](\d+[a-z]?)$/i;

    if (rxNum.test(last)) {
      collector = last;
      parts.pop();
    } else if (rxSetNum.test(last)) {
      const sm = last.match(rxSetNum)!;
      if (!set) set = sm[1].toUpperCase();
      collector = sm[2];
      parts.pop();
    }

    let name = parts.join(" ").replace(/\s+/g, " ").trim();
    name = stripLeadingCount(name);
    name = normalizeApostrophes(name);
    if (!name || looksLikeSectionHeader(name)) continue;

    out.push({
      name,
      count,
      set,
      number: collector,
      foil
    });
  }
  return out;
}

export async function parseDeckFromUrl(url: string): Promise<NormalDeck> {
  const hit = detectDeckSource(url);
  if (!hit) throw new Error("Unsupported deck URL");
  let deck: NormalDeck;
  switch (hit.kind) {
    case "archidekt": deck = await parseArchidekt(url, hit.id!); break;
    case "moxfield": deck = await parseMoxfield(url); break;
    case "tappedout": deck = await parseTappedOut(url); break;
    case "manabox": deck = await parseManaBox(url); break;
    case "aetherhub": deck = await parseAetherHub(url); break;
    case "tcgplayer": deck = await parseTCGplayer(url); break;
    case "edhrec":    deck = await parseEDHREC(url); break;
    default: throw new Error("Unsupported deck URL");
  }
  return dedupeDeck(deck);
}

export function parseDeckFromText(text: string): NormalDeck {
  const slab = text.replace(/\r/g,"");
  const commander = extractSectionNames(slab, /(Commander|Commanders)/i);
  const companion = extractCompanion(slab);
  const main = extractSectionCards(slab, /(Mainboard|Maindeck|Decklist|Main\s*Deck)/i) || parseCountNameLines(slab);
  const side = extractSectionCards(slab, /(Sideboard)/i);
  const maybe = extractSectionCards(slab, /(Maybeboard)/i);
  return dedupeDeck({
    commander: commander.length ? commander : undefined,
    companion,
    main,
    side: side?.length ? side : undefined,
    maybeboard: maybe?.length ? maybe : undefined,
  });
}

function extractSectionCards(text: string, header: RegExp): NormalCard[] | null {
  const parts = text.split(header);
  if (parts.length < 2) return null;
  const tail = parts.slice(2).join("");
  const blk = tail.split(/(?:Commander|Mainboard|Maindeck|Decklist|Sideboard|Maybeboard|Companion|Main\s*Deck)/i)[0] || tail;
  const cards = parseCountNameLines(blk);
  return cards.length ? cards : null;
}
function extractSectionNames(text: string, header: RegExp): string[] {
  const cards = extractSectionCards(text, header);
  return cards ? cards.map(c => c.name) : [];
}
function extractCompanion(text: string): string | null {
  const m = text.match(/Companion\s*:?\s*([\w\s'’\-:,]+)/i);
  return m ? m[1].trim() : null;
}

// Helper: prefer embedded CDN URLs; fall back to CDN-by-id
function pickCardImages(c: any): {
  small: string | null;
  normal: string | null;
  large: string | null;
} {
  const fromObj =
    c?.image_uris?.normal ||
    c?.image_uris?.large ||
    c?.image_uris?.small
      ? {
          small: c?.image_uris?.small ?? null,
          normal: c?.image_uris?.normal ?? c?.image_uris?.large ?? null,
          large: c?.image_uris?.large ?? c?.image_uris?.normal ?? null,
        }
      : null;

  if (fromObj) return fromObj;

  const face = Array.isArray(c?.card_faces) ? c.card_faces[0] : null;
  if (face?.image_uris) {
    return {
      small: face.image_uris.small ?? null,
      normal: face.image_uris.normal ?? face.image_uris.large ?? null,
      large: face.image_uris.large ?? face.image_uris.normal ?? null,
    };
  }

  // Fallback: construct CDN URLs by id
  if (c?.id) {
    return {
      small: scryImageUrl(c.id, "small"),
      normal: scryImageUrl(c.id, "normal"),
      large: scryImageUrl(c.id, "large"),
    };
  }
  return { small: null, normal: null, large: null };
}

// Normalize scry responses (Response or JSON)
async function normalizeScry<T = any>(resOrJson: any): Promise<T> {
  return typeof resOrJson?.json === "function" ? await resOrJson.json() : (resOrJson as T);
}

/* Optional Scryfall enrichment (now using our internal backend) */
export async function enrichWithScryfall(deck: NormalDeck) {
  async function lookup(name: string, set?: string | null, cn?: string | null) {
    try {
      let card: any = null;

      // Backend-first via scryApi (prints)
      if (set && cn) {
        card = await backendByPrint(String(set).toLowerCase(), String(cn), name);
      }
      if (!card) {
        card = await backendByExactName(name);
      }

      if (!card) return {};
      return mapBackendCardToExtras(card);
    } catch {
      return {};
    }
  }

  // Concurrency limiter to avoid hammering the backend
  const mapWith = async (arr?: NormalCard[]) => {
    if (!arr || !arr.length) return arr;
    const out: NormalCard[] = [];
    const CONCURRENCY = 6;
    let i = 0;
    while (i < arr.length) {
      const slice = arr.slice(i, i + CONCURRENCY);
      const chunk = await Promise.all(
        slice.map(async (c) => ({ ...c, ...(await lookup(c.name, c.set ?? null, c.number ?? null)) }))
      );
      out.push(...chunk);
      i += CONCURRENCY;
    }
    return out;
  };

  return {
    ...deck,
    main: (await mapWith(deck.main)) || [],
    side: await mapWith(deck.side),
    maybeboard: await mapWith(deck.maybeboard),
  };
}

// Resolve backend base robustly: prefer emulators only when VITE_USE_EMULATORS=true
function resolveScryCardsBase() {
  const env: any = (import.meta as any).env || {};
  const useEmu = String(env.VITE_USE_EMULATORS || "").toLowerCase() === "true";
  const explicit = env.VITE_SCRY_CARDS_URL as string | undefined;

  if (useEmu) return explicit || "http://localhost:5000/api/scry/cards";
  if (explicit && !/localhost|127\.0\.0\.1/.test(explicit)) return explicit;

  const api = env.VITE_API_BASE as string | undefined;
  if (api) return `${api.replace(/\/$/, "")}/api/scry/cards`;

  return `${globalThis.location?.origin || ""}/api/scry/cards`;
}
const SCRY_CARDS_BASE = resolveScryCardsBase();

async function scryCardsGet<T = any>(
  path: string,
  params?: Record<string, string | number | null | undefined>
): Promise<T | null> {
  if (!SCRY_CARDS_BASE) return null;
  try {
    const p = path.replace(/^\//, "");
    const q = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";
    // handle absolute and relative bases
    const url =
      SCRY_CARDS_BASE.startsWith("http")
        ? `${SCRY_CARDS_BASE.replace(/\/$/, "")}/${p}${q}`
        : `${SCRY_CARDS_BASE.replace(/\/$/, "")}/${p}${q}`;

    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) throw new Error(`scry-cards ${path} failed: ${r.status}`);
    return (await r.json()) as T;
  } catch (e) {
    console.warn("[scry-cards] fallback (deckImporters):", path, e);
    return null;
  }
}

// Exact-name lookup (search exact phrase)
async function backendByExactName(name: string) {
  const nm = normalizeApostrophes(name).trim();
  try {
    const res = await scryApi.searchPrints(`name:"${nm}"`);
    const arr = sortByRelease(toCardArray(res));
    return arr?.[0] || null;
  } catch {}
  return null;
}

// Lookup by set + collector number (backend prints; no "!" exact)
async function backendByPrint(set: string, cn: string, name?: string | null) {
  const nm = name ? normalizeApostrophes(name).trim() : null;
  try {
    const q = nm ? `name:"${nm}" set:${set} cn:${cn}` : `set:${set} cn:${cn}`;
    const res = await scryApi.searchPrints(q);
    const arr = sortByRelease(toCardArray(res));
    return arr?.[0] || null;
  } catch {}
  return null;
}

function sortByRelease<T extends { released_at?: string; set?: string; collector_number?: string }>(list: T[]) {
  return [...(list || [])].sort((a, b) => {
    const da = a.released_at ? Date.parse(a.released_at) : 0;
    const db = b.released_at ? Date.parse(b.released_at) : 0;
    if (db !== da) return db - da;
    const sa = a.set ?? "";
    const sb = b.set ?? "";
    if (sb !== sa) return sb.localeCompare(sa);
    const ca = parseInt((a as any).collector_number || "0", 10) || 0;
    const cb = parseInt((b as any).collector_number || "0", 10) || 0;
    return cb - ca;
  });
}

// Normalize for matching (strip accents/punctuation)
function nameKey(s: string): string {
  return String(s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // accents
    .replace(/[’‘`´]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/[^a-z0-9]+/gi, "") // remove all non-alnum
    .toLowerCase();
}